"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { getPlayerColor } from "@/app/utils";
import { useAuth } from "@/components/AuthProvider";

export default function Frontpage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const router = useRouter();
  const { user, loading: authLoading, logout } = useAuth();

  const fetchGames = async (pageNum, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const res = await fetch(`/api/games?page=${pageNum}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch games (${res.status})`);
      }
      const data = await res.json();
      if (pageNum === 1) {
        setGames(data.games);
      } else {
        setGames((prev) => [...prev, ...data.games]);
      }
      setHasMore(data?.pagination?.hasMore);
    } catch (error) {
      console.error("Error fetching games:", error);
      setLoadError(error?.message || "Failed to fetch games");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchGames(1);
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch("/api/leaderboard");
      const data = await res.json();
      setLeaderboard(data.players || []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  // Auto-refresh games list every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchGames(1, true); // Silent refresh to avoid page jumping
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGames(nextPage);
  };

  // Helper function to check if a player is the winner
  const isWinner = (game, player) => {
    const gameOverAction = game.actions?.find(
      (action) => action.action === "game_over" && action.playerId === player.id
    );

    return gameOverAction;
  };

  return (
    <div className="p-8 flex justify-center items-center min-h-screen">
      <div className="">
        <div className="flex justify-center">
          <img
            src="/backtolife.png"
            alt="Back to Life"
            className="w-1/2 max-h-[40vh]"
          />
        </div>
        <p className="text-gray-400 mt-4 text-center">
          A turn-based strategy game by Jonatan, Noel and Matheo Littke
        </p>

        {/* Auth section */}
        <div className="mt-6 flex justify-center">
          {authLoading ? (
            <div className="text-gray-400">Loading...</div>
          ) : user ? (
            <div className="flex items-center gap-4 bg-slate-800 px-4 py-2 rounded-lg border border-slate-700">
              <div>
                <span className="text-gray-400">Logged in as </span>
                <span className="font-bold">{user.username}</span>
                <span className="text-gray-500 text-sm ml-2">
                  ({user.wins}W / {user.losses}L)
                </span>
              </div>
              <button
                onClick={logout}
                className="text-red-400 hover:text-red-300 text-sm"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Link
                href="/login"
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition-colors"
              >
                Login
              </Link>
              <Link
                href="/register"
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded transition-colors"
              >
                Register
              </Link>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <div className="mt-4">
            <button
              onClick={() => {
                router.push("/create-game");
              }}
              disabled={!user}
              className={`py-2 px-3 rounded ${
                user
                  ? "bg-slate-700 border border-slate-500 hover:bg-slate-600"
                  : "bg-slate-800 border border-slate-700 text-gray-500 cursor-not-allowed"
              }`}
            >
              New game
            </button>
            {!user && (
              <p className="text-gray-500 text-sm mt-1 text-center">
                Login to create a game
              </p>
            )}
          </div>
        </div>
        <div>
          <h2 className="text-md mt-12 font-bold">Join a game</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">
            {loadError && (
              <p className="text-gray-400">
                {loadError}. Refresh the page to retry.
              </p>
            )}
            {!loadError && !loading && games?.length === 0 && (
              <p className="text-gray-400">No games yet.</p>
            )}
            {games?.map((game) => (
              <Link
                key={game.id}
                href={`/game/${game.id}`}
                className="block bg-slate-800 border border-slate-700 rounded-lg p-4 hover:bg-slate-700 transition-colors"
              >
                <h3 className="text-lg font-bold mb-2">
                  {game.name ? game.name : `Game ${game.id}`}
                </h3>
                <div className="space-y-2">
                  {game.players.map((player) => (
                    <div key={player.id} className="flex items-center gap-2">
                      <div
                        className={`w-3 h-3 rounded-full ${getPlayerColor(
                          player.id
                        )}`}
                      />
                      <span>{player.username}</span>
                      {isWinner(game, player) && (
                        <span className="text-amber-500 font-bold text-sm">
                          • Winner
                        </span>
                      )}
                      <div className="ml-auto text-sm">
                        <span className="font-bold">{player.kills || 0}</span>
                        <span className="text-slate-400 ml-1">kills</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          {loading && (
            <div className="flex justify-center mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500"></div>
            </div>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center mt-20">
              <button
                onClick={loadMore}
                className="bg-slate-700 border border-slate-500 py-2 px-4 rounded hover:bg-slate-600 transition-colors"
              >
                Show more
              </button>
            </div>
          )}
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="mt-12">
            <h2 className="text-md font-bold">Leaderboard</h2>
            <div className="mt-4 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-sm text-slate-400">
                    <th className="px-4 py-2 w-12">#</th>
                    <th className="px-4 py-2">Player</th>
                    <th className="px-4 py-2 text-right">Wins</th>
                    <th className="px-4 py-2 text-right">Losses</th>
                    <th className="px-4 py-2 text-right">Kills</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((player, index) => (
                    <tr
                      key={player.id}
                      className="border-b border-slate-700/50 last:border-b-0 hover:bg-slate-700/50"
                    >
                      <td className="px-4 py-2 text-slate-400">{index + 1}</td>
                      <td className="px-4 py-2 font-medium">{player.username}</td>
                      <td className="px-4 py-2 text-right text-green-400">{player.wins}</td>
                      <td className="px-4 py-2 text-right text-red-400">{player.losses}</td>
                      <td className="px-4 py-2 text-right text-slate-300">{player.totalKills}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
